import React, { FC, useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { Button, Popconfirm, message } from 'antd';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import DetailPageHeader from '../detail_page_header/DetailPageHeader';
import { nullSafeGet, nullSafeGetOrElse, getEntityById } from '../../utils/DataAccessUtils';
import {
	fetchProcedureDraft,
	AOP_VERSIONS_FIRST_PAGE,
	fetchProcedureVersions,
} from '../../thunks/agent_procedures_thunks';
import { versionKey } from '../../reducers/agent_procedure_versions_reducer';
import { isSameAsVersion } from './utils/AOPVersionUtils';
import { deleteAgentScenario, readAgentScenarios } from '../../thunks/agent_scenarios_thunks';
import { withUserType } from '../withUserType';

/*
  Not the list page's collection.

  These screens want one thing from `GET /scenarios` — the entry for a given
  procedure. Sharing `agentScenariosIndex` with the paginated list meant an
  unpaginated read overwriting its page and its count, which is the
  shared-collection trap CLAUDE.md names.

  The entry is resolved through this collection's ids rather than the slice's
  `records`, which is merged into and never pruned: a scenario deleted in
  another session would otherwise survive a refetch that no longer returns it.
*/
const SCENARIO_TC = 'aopProcedureScenario';

/**
 * Detail page for one Agent Operating Procedure: the header, and the tabs that
 * hang off it. The draft is loaded once here so switching tabs doesn't refetch.
 *
 * Delete goes through the scenario, not the procedure — the service removes the
 * pair together and has no delete for a procedure on its own.
 */
const AOPDetailPage: FC<any> = ({
	companyConfig,
	childRoutes,
	history,
	match,
	userType,
	draft,
	isLoading,
	scenarioForProcedure,
	latestPublished,
	loadDraft,
	loadVersions,
	loadScenarios,
	removeScenario,
}): React.ReactElement => {
	const [isDeleting, setIsDeleting] = useState(false);

	const procedureId = useMemo(() => match.params.id, [match.params.id]);
	const backLink = useMemo(() => `/${userType}/workflows/agent_operating_procedures`, [userType]);
	const basePath = useMemo(
		() => `/${userType}/workflows/agent_operating_procedures/detail`,
		[userType]
	);

	/*
	  Follows the procedure, not the mount.

	  This is one route with an id in it, so going from one procedure's detail
	  page straight to another's reuses this component — and a mount-only read
	  left the second one showing the first one's draft, versions and routing
	  until a reload. Nothing in the app links procedure to procedure today, but
	  a pasted or edited URL does it, and the cost of being right is the
	  dependency list.
	*/
	React.useEffect(() => {
		// The version history and the live version's document are loaded here
		// rather than in the Procedure tab: the header's status needs them too, so
		// they have to be there whichever tab you land on.
		loadDraft(procedureId).catch(() => undefined);
		/*
		  Hydrated, and in parallel with the draft.

		  The live version's document is what the header compares the draft
		  against, and its number is only on the draft — so this used to wait for
		  the draft and then read that one version on its own. The list already
		  holds it: asked for hydrated, the rows carry their documents, and the
		  draft only has to name which of them is live rather than go and get it.

		  A live version older than this first page is read by the Procedure
		  tab's effect for versions it does not hold, which is what that is for.
		*/
		loadVersions(procedureId).catch(() => undefined);
		// The header's Delete acts on the scenario, so the list has to be loaded to
		// know which one points at this procedure.
		loadScenarios(procedureId).catch(() => undefined);
	}, [loadDraft, loadScenarios, loadVersions, procedureId]);

	const tabs = useMemo(
		() => [
			{ key: 'procedure', name: 'Procedure' },
			{ key: 'scenarios', name: 'Scenarios' },
			{ key: 'workorders', name: 'Work Orders' },
			{ key: 'activity', name: 'Activity' },
		],
		[]
	);

	const handleTabChange = useCallback(
		(key) => history.push(`${basePath}/${procedureId}/${key}`),
		[basePath, history, procedureId]
	);

	// The last path segment, not a substring search: 'procedure' occurs inside
	// 'agent_operating_procedures', so an indexOf test marks the Procedure tab
	// active on every tab of this page.
	const checkTabActive = useCallback(
		(key) => {
			const segments = history.location.pathname.split('/').filter((segment) => segment.length > 0);
			return segments[segments.length - 1] === key;
		},
		[history.location.pathname]
	);

	const onDelete = useCallback(() => {
		if (!scenarioForProcedure) {
			message.error('This procedure has no scenario, so it cannot be deleted here.');
			return;
		}

		setIsDeleting(true);
		removeScenario(scenarioForProcedure.scenario.id)
			.then(() => {
				message.success('Procedure deleted successfully!');
				history.replace(backLink);
			})
			.catch(() => undefined)
			.then(() => setIsDeleting(false));
	}, [backLink, history, removeScenario, scenarioForProcedure]);

	const procedure = nullSafeGet('procedure', draft);
	const publishedVersion = nullSafeGet('procedure.published_version', draft);
	// null means never published; undefined means the field was missing. Both are
	// unpublished — testing only for null printed "Published vundefined".
	const isPublished = publishedVersion !== null && publishedVersion !== undefined;

	// The service reports no "this draft has unpublished changes" flag, so the
	// draft is compared against the live version's frozen copy here. Until that
	// copy has loaded there is nothing to compare, and the suffix stays off
	// rather than guessing.
	const hasUnpublishedChanges =
		isPublished && latestPublished
			? !isSameAsVersion(nullSafeGet('prose', draft), nullSafeGet('plan', draft), latestPublished)
			: false;

	const status = useMemo(() => {
		if (!procedure) {
			return null;
		}

		return (
			<div className="flex flex-row items-center gap-xs">
				<span
					className={`h-2 w-2 shrink-0 rounded-full ${
						isPublished ? 'bg-green-500' : 'bg-calendulaGold-5'
					}`}
				/>
				<span>
					{isPublished ? `Published v${publishedVersion} · live` : 'Draft'}
					{hasUnpublishedChanges ? ' · draft in progress' : ''}
				</span>
			</div>
		);
	}, [hasUnpublishedChanges, isPublished, procedure, publishedVersion]);

	return (
		/*
		  Keyed by the procedure, so moving from one to another remounts this
		  subtree instead of reusing it.

		  `/detail/:id` is one route: React Router keeps the page and its tabs
		  mounted when only the id changes, and four things rode on that — a late
		  scenario read for the old procedure replacing the shared collection, so
		  the new one looked unrouted and could not be deleted; Activity and Work
		  Orders still showing the old procedure's rows, because they load on
		  mount; and the Scenarios tab's open edit form keeping the old values,
		  so Apply wrote them onto the new scenario's scope.
		*/
		<DetailPageHeader
			key={procedureId}
			exactPath={`${basePath}/:id`}
			redirectPath={`${basePath}/${procedureId}/procedure`}
			backLinkText={'Back to Agent Operating Procedures'}
			backLinkTo={backLink}
			title={
				isLoading && !procedure ? null : (
					<div className="flex flex-col gap-2">
						{nullSafeGetOrElse('name', procedure, 'Agent Operating Procedure')}
						<div className="text-sm">{status}</div>
					</div>
				)
			}
			subtitle={isLoading && !procedure ? null : nullSafeGetOrElse('description', procedure, '')}
			actions={
				<div className="flex flex-row items-center gap-2">
					<Button
						key="edit"
						ghost={true}
						icon={<EditOutlined />}
						onClick={() => handleTabChange('scenarios')}
					>
						Edit
					</Button>
					<Popconfirm
						title="Delete this procedure and the scenario routing to it? Both are removed together."
						onConfirm={onDelete}
						okText="Yes, Delete"
						okButtonProps={{ danger: true }}
						cancelText="Cancel"
					>
						<Button key="delete" ghost={true} icon={<DeleteOutlined />} loading={isDeleting}>
							Delete
						</Button>
					</Popconfirm>
				</div>
			}
			tabs={tabs}
			backgroundColor={nullSafeGet('detail.config.theme.navblock', companyConfig)}
			handleTabChange={handleTabChange}
			checkTabActive={checkTabActive}
			childRoutes={childRoutes}
			childBodyBackgroudColor={'#FFFFFF'}
		/>
	);
};

const mapStateToProps = (state, ownProps) => {
	const procedureId = nullSafeGet('match.params.id', ownProps);
	const scenariosSlice = nullSafeGetOrElse('agent_scenarios', state, {});
	const scenarioRecords = nullSafeGetOrElse('records', scenariosSlice, {});
	/*
	  The ids this collection's last read returned, not every scenario ever
	  cached.

	  `records` is merged into and never pruned, so a scenario deleted in
	  another session stays in it after a refetch that no longer returns it —
	  and the header would go on offering to delete routing that is gone. The
	  collection's ids are what the service last said, so the lookup is made
	  through them. Bracket access: a collection's name is a key, not a path
	  segment.
	*/
	const scenarioIds = nullSafeGetOrElse('recordsIds', scenariosSlice[SCENARIO_TC], [] as string[]);
	const draft = getEntityById(procedureId, state.agent_procedures, null);
	const publishedVersion = nullSafeGet('procedure.published_version', draft);
	const versions = nullSafeGetOrElse('agent_procedure_versions.versions', state, {});

	return {
		companyConfig: state.company_config,
		draft,
		// Bracket access: the store key is a composite identifier, not a path.
		latestPublished:
			publishedVersion === null || publishedVersion === undefined
				? null
				: versions[versionKey(procedureId, publishedVersion)],
		isLoading: nullSafeGetOrElse('agent_procedures.fetching', state, false),
		scenarioForProcedure: scenarioIds
			.map((id) => scenarioRecords[id])
			.filter((entry) => entry && entry.procedure && entry.procedure.id === procedureId)[0],
	};
};

const mapDispatchToProps = (dispatch) => ({
	loadDraft: (procedureId: string) => dispatch(fetchProcedureDraft(procedureId)),
	/*
	  Summaries only. The header needs the live version's document to say
	  whether the draft has moved on, and that one is read by id above —
	  pulling every version's plan to answer it would be the heavier way round.
	*/
	loadVersions: (procedureId: string) =>
		dispatch(
			fetchProcedureVersions(procedureId, { hydrate: true, limit: AOP_VERSIONS_FIRST_PAGE })
		),
	loadScenarios: (procedureId: string) =>
		dispatch(
			/*
			  One request, one record. `GET /scenarios` takes `procedure_id`, so
			  this asks for the scenario routing to this procedure rather than
			  reading the list and looking through it.
			*/
			readAgentScenarios(
				{ procedure_id: procedureId },
				SCENARIO_TC,
				{ current: 1, pageSize: 1 },
				null,
				null,
				false
			)
		),
	removeScenario: (scenarioId: string) => dispatch(deleteAgentScenario(scenarioId)),
});

export default withUserType(
	withRouter<any, any>(connect(mapStateToProps, mapDispatchToProps)(AOPDetailPage))
);
